<template>
  <div
    class="volume-container"
    :class="{ 'is-open': isOpen }"
  >
    <div class="volume-container__header" @click.self="toggleCollapse">
      <div class="volume-container__header--left" @click.prevent="toggleCollapse">
        <span class="header-label">Volume</span>
        <transition name="el-fade-in-linear">
          <VolumeProgressChart
            v-if="!isOpen"
            :volumes="globalVolume"
            id-type="idbased"
            :chart-colors="chartColors"
            hide-progress
            tooltip-placement="top"
            :is-open="isOpen"
            @click.self="toggleCollapse"
          />
        </transition>
        <svg-icon style="position: absolute; right: 10px" size="small" name="collapse" @click.self="toggleCollapse" />
      </div>
      <div class="volume-container__header--right l-flex-row" @click.self="toggleCollapse('action')">
        <Tooltip placement="top">
          <template slot="content">
            <div v-if="breakDownDisabled">
              {{ !demographicBreakdown ? 'Demographic Breakdown is disabled' : 'There is no user reached ...' }}
            </div>
            <div v-else>
              See breakdown
            </div>
          </template>
          <div style="margin-right: 10px">
            <el-button
              size="small"
              type="primary"
              circle
              :disabled="breakDownDisabled"
              style="padding: 3px"
              @click="openDialog"
            >
              <template v-if="!breakdownLoading">
                <svg-icon v-show="!breakDownDisabled" name="breakdown" />
                <svg-icon v-show="breakDownDisabled" name="breakdown-dark" />
              </template>
              <Spinner v-else width="25" height="25" />
            </el-button>
          </div>
        </Tooltip>
        <el-button
          size="small"
          type="primary"
          class="volume-container__save-audience big-screen"
          :class="{ 'ui-disabled': disabledCreateAudience }"
          @click.prevent="checkCreateAudienceForm"
        >
          Save Audience
        </el-button>
        <Tooltip content="Save Audience" placement="top">
          <el-button
            size="small"
            type="primary"
            style="padding: 3px"
            circle
            class="volume-container__save-audience small-screen"
            :class="{ 'ui-disabled': disabledCreateAudience }"
            @click.prevent="checkCreateAudienceForm"
          >
            <svg-icon name="add" />
          </el-button>
        </Tooltip>
      </div>
    </div>
    <el-collapse-transition>
      <div v-if="isOpen">
        <div class="builder-content builder-volume scrollbar is-scrollable">
          <VolumeProgressChart :is-open="isOpen" :volumes="globalVolume" id-type="idbased" :chart-colors="chartColors" />
        </div>

        <div v-if="xdevice" class="builder-content builder-volume scrollbar is-scrollable">
          <VolumeProgressChart :is-open="isOpen" :volumes="globalVolume" id-type="xdevice" :chart-colors="chartColors" />
        </div>
      </div>
    </el-collapse-transition>
    <Dialog title="Breakdown" :visible="dialogVisible" width="90%" top="50px" @close="dialogVisible = false">
      <div>
        <Breakdown :visible="dialogVisible" />
      </div>
    </Dialog>
  </div>
</template>
<script>
import { CollapseTransition, Button, Tooltip, Dialog } from 'element-ui'
import VolumeProgressChart from './VolumeProgressChart.vue'
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex'
import { isEqual, isNil } from 'lodash'
import { VOLUME_CHART_COLORS } from '@/utils/constants'
import Breakdown from './Breakdown.vue'
import Spinner from '@/components/common/Spinner'

export default {
    props: {
        globalVolume: {
            default: () => ({}),
            type: Object,
        },
    },

    components: {
        'el-collapse-transition': CollapseTransition,
        Tooltip,
        VolumeProgressChart,
        'el-button': Button,
        Dialog,
        Breakdown,
        Spinner,
    },

    data() {
        return {
            isOpen: true,
            dialogVisible: false,
            chartColors: VOLUME_CHART_COLORS,
        }
    },

    computed: {
        ...mapState('builder', {
            builderAudienceFormula: state => state.formula,
            breakdown: state => state.formulaBreakdown,
            breakdownLoading: state => state.formulaBreakdownLoading,
            audienceInEditionId: state => state.audienceInEditionId,
        }),

        isEditionMode() {
            return this.audienceInEditionId !== undefined && this.audienceInEditionId !== null
        },

        ...mapState('ui/settings', {
            xdevice: state => state.general.xdevice,
            firstPartyCookie: state => state.general.firstPartyCookie,
            demographicBreakdown: state => state.general.demographicBreakdown,
        }),

        ...mapGetters('builder', ['inEditionMode', 'audienceInEdition']),
        ...mapGetters('account', ['getIsAudienceCreationAllowed']),

        breakDownDisabled() {
            return !!this.breakdownLoading || this.volumeTotal === 0 || this.volumeTotal === null || !this.demographicBreakdown
        },

        volumeTotal() {
            return isNil(this.globalVolume) ? null : this.globalVolume.total_desktop
        },

        disabledCreateAudience() {
            return (
                !this.getIsAudienceCreationAllowed({ mode: 'builder' }) ||
                (this.builderAudienceFormula?.isEmpty) ||
                this.hasOnlyDisabledCriteria
            )
        },

        hasOnlyDisabledCriteria() {
            const flatCriteria = []
            this.builderAudienceFormula?.boxes.forEach(box => {
                flatCriteria.push(...box?.criteria)
            })
            return flatCriteria.every(c => c.disabled)
        },

        editedVersionHasChange() {
            if (!this.inEditionMode) return true // return true in create audience mode
            if (!this.inEditionMode && !this.audienceInEdition) return true

            let cleanedAudience = []

            const formatAudience = (audience) => {
                const audienceOverrided = this.audienceGenericFormulaType(audience) ? this.changeAudienceName(audience) : audience
                cleanedAudience.push(audienceOverrided)
            }
            //If there is Only 1 item it's in an object and can't forEach on it
            if (Array.isArray(this.audienceInEdition?.genericFormula)) this.audienceInEdition?.genericFormula.forEach(el => formatAudience(el))
            else formatAudience(this.audienceInEdition?.genericFormula)

            // When there is only one
            if (!Array.isArray(this.builderAudienceFormula?.v1)) {
                if (cleanedAudience[0] === '|' || cleanedAudience[0] === '&' || cleanedAudience[0] === '!') {
                    if (cleanedAudience.length === 2) {
                        cleanedAudience = this.audienceGenericFormulaType(cleanedAudience[1]) ? this.changeAudienceName(cleanedAudience[1]) : cleanedAudience[1]
                    }
                } else {
                    cleanedAudience = this.audienceGenericFormulaType(cleanedAudience[0]) ? this.changeAudienceName(cleanedAudience[0]) : cleanedAudience[0]
                }
            }

            return isEqual(cleanedAudience, this.builderAudienceFormula.v1)
        }
    },

    beforeMount() {
        const state = localStorage.getItem('volumeContainerIsOpenState')
        this.isOpen = state === 'true'
    },
    mounted() {
        this.initOpenContainer(this.isOpen)
    },

    methods: {
        ...mapActions('ui/builder', ['createAudienceForm']),
        ...mapMutations('ui/builder', ['setDisplayModal']),

        checkCreateAudienceForm() {
            if (this.builderAudienceFormula.boxes.some(box => box.isEmpty)) {
                this.setDisplayModal({ item: 'warning', display: true })
            } else {
                if (this.isEditionMode) {
                    this.$emit('show-before-edit-modal', true)
                } else {
                    this.createAudienceForm()
                }
            }
        },

        audienceGenericFormulaType(audience) {
            return audience?.type === 'Segment' || audience?.type === 'WcmClick' || audience?.type === 'Techno' || audience?.type === 'Audience'
        },

        changeAudienceName(audience) {
            const behavioralName = `behavioral__${audience?.type.toLowerCase()}_${audience.ids[0]}`
            const wcmName = `media__campaigns__clicked_${audience.ids[0]}`
            if (audience?.type === 'Segment') return { ...audience, name: behavioralName }
            else if (audience?.type === 'WcmClick') return { ...audience, name: wcmName }
            else if (audience?.type === 'Techno' || audience?.type === 'Audience') {
                const ids = audience.ids.map(id => {
                    id = id.toString()
                    return id
                })
                return {
                    ...audience,
                    ids
                }
            }
            return audience
        },

        handleChange(props) {
            if (props === '1') {
                localStorage.setItem('volumeContainerIsOpenState', true)
            } else {
                localStorage.setItem('volumeContainerIsOpenState', false)
            }
        },

        toggleCollapse(action = '') {
            if (action !== 'action') {
                this.isOpen = !this.isOpen
                const formulaContainer = document.querySelector('.builder-formula')
                if (formulaContainer) {
                    formulaContainer.classList.toggle('volume-container-open', this.isOpen)
                }
                localStorage.setItem('volumeContainerIsOpenState', this.isOpen)
            }
        },
        initOpenContainer(isOpen){
            const formulaContainer = document.querySelector('.builder-formula')
            if (formulaContainer) {
                formulaContainer.classList.toggle('volume-container-open', isOpen)
            }
        },

        openDialog() {
            if (this.breakDownDisabled) return
            this.$emit('trigger-breakdown')
            this.dialogVisible = true
        }
    }
}
</script>

<style lang="scss">

.volume-container {
  margin-bottom: 40px;
  padding: 0 16px;
  font-size: 1rem;
  background: #fff;
  border: none;
  border-radius: 20px;
  position: relative;
  .builder-volume {
    padding: 0 10px;
  }
  &__wrap {
    border-bottom-right-radius: 20px;
    border-bottom-left-radius: 20px;
    border: none !important;
  }
  &__header {
    border-top-right-radius: 20px;
    border-top-left-radius: 20px;
    border: none !important;
    height: 72px;
    &:hover {
      cursor: pointer;
    }
    &:not(.is-active) {
      border-radius: 20px;
    }
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    &--left {
      padding: 0 0 0 10px;
      display: flex;
      justify-content: start;
      gap: 10px;
      align-items: center;
      span.header-label {
        color: #000 !important;
        font-weight: 500;
        font-size: 1.2rem
      }
    }
    &--right {
      margin-right: 20px;
      padding: 8px 12px;
      align-items: center;
    }
  }
  &__save-audience {
    padding: 8px 12px;
    svg {
      fill: white;
    }
    &.ui-disabled {
      cursor: not-allowed !important;
      &:hover {
        cursor: not-allowed !important;
      }
      svg {
        fill: gray;
      }
    }
    &.big-screen {
      display: block;
    }
    &.small-screen {
      display: none;
    }
  }
  .el-dialog {
    border-radius: 16px;

    .el-dialog__header {
      padding: 20px 40px 10px;
    }
  }
  .el-button {
    &.is-disabled {
      svg path {
        stroke: var(--color-text-inactive) !important
      }
    }
  }
}

@media (min-width: 1200px) and (max-width: 1439px) {
  .volume-container {
    &__save-audience {
      &.big-screen {
        display: none;
      }
      &.small-screen {
        display: block;

      }
    }
  }
}
</style>
